import { LoaderFunctionArgs, useParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Stack,
  Text,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { format, parseISO } from 'date-fns';

import {
  ActionIconButton,
  CheckOutline,
  CopyOutline,
  DEFAULT_DATE_FORMAT,
  EmptyState,
  InviteOutline as Invite,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import ProfileField from 'app/components/UI/ProfileField';
import InviteIndividualModal from 'app/components/Individuals/InviteIndividualModal';

import { Status } from 'app/constants/individual';
import useFetchIndividual, { fetchIndividualQuery } from 'app/hooks/useFetchIndividual';

export const adminProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchIndividualQuery(params.adminId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function AdminProfile() {
  const queryClient = useQueryClient();
  const { adminId } = useParams();

  const { isOpen: isInviteOpen, onOpen: onOpenInvite, onClose: onCloseInvite } = useDisclosure();

  const { data: admin, isInitialLoading: isLoadingAdmin } = useFetchIndividual(adminId);

  const { onCopy, hasCopied } = useClipboard(admin?.friendlyId ?? '');

  const handleInvalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchIndividual', adminId] });
    queryClient.refetchQueries({ queryKey: ['fetchIndividual', adminId] });
  };

  const username = admin?.firstName ? `${admin.firstName} ${admin.lastName}` : admin?.email;

  if (isLoadingAdmin) {
    return (
      <Center width="full" height="full" flexGrow={1}>
        <Spinner color="brand.purple" size="xl" mb="50px" />
      </Center>
    );
  }

  if (!admin) {
    return (
      <AuthenticatedPageWrapper title="Admin Not Found">
        <EmptyState text="The requested admin was not found." />
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper title={username || 'Admin Profile'}>
      <Flex flexDir="column" gap="30px" flexGrow={1}>
        <InviteIndividualModal
          selectedIndividuals={[admin.id]}
          isOpen={isInviteOpen}
          onClose={onCloseInvite}
          onInvite={handleInvalidateQuery}
        />

        {/* Header */}
        <Flex gap="20px" align="flex-start" justify="space-between">
          <Stack spacing="10px">
            <Text as="h2" textStyle="h2" color="brand.darkPurple" wordBreak="break-word">
              {username}
            </Text>
            <Text
              color="brand.black"
              textStyle="smallRegular"
            >{`Aslan ID: ${admin.friendlyId}`}</Text>
          </Stack>

          <Button
            width="auto"
            size="sm"
            flexShrink={0}
            leftIcon={<Invite boxSize="20px" />}
            onClick={onOpenInvite}
            isDisabled={admin.status !== 'CREATED' && admin.status !== 'INVITED'}
          >
            Invite
          </Button>
        </Flex>

        {/* Profile card */}
        <Flex
          flexDir="column"
          borderRadius="10px"
          border="1px solid"
          borderColor="brand.lightGray1"
          mx={{ base: 0, md: '60px', xl: '100px' }}
          flexWrap="wrap"
        >
          {/* Card header */}
          <Flex w="full" justifyContent="start" p="20px">
            <Text color="brand.purple" textStyle="h5Bold">
              Profile
            </Text>
            {admin.status && (
              <Badge colorScheme={`badge.${Status[admin.status].color}`} ml="auto">
                {Status[admin.status].value}
              </Badge>
            )}
          </Flex>

          {/* General details */}
          <Flex
            w="full"
            flexDir={{ base: 'column', md: 'row' }}
            justifyContent="start"
            p="20px"
            borderTop="1px solid"
            borderColor="brand.lightGray1"
            gap="20px"
          >
            <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
              General details
            </Text>

            <Grid
              templateColumns="1fr 1fr"
              rowGap="15px"
              columnGap="20px"
              wordBreak="break-word"
              w="full"
            >
              <GridItem>
                <ProfileField label="First name" value={admin.firstName} />
              </GridItem>
              <GridItem>
                <ProfileField label="Last name" value={admin.lastName} />
              </GridItem>
              <GridItem>
                <ProfileField label="Company email" value={admin.email} />
              </GridItem>
              <GridItem>
                <ProfileField label="Job title" value={admin.jobTitle} />
              </GridItem>
            </Grid>
          </Flex>

          {/* Admin details */}
          <Flex
            w="full"
            flexDir={{ base: 'column', md: 'row' }}
            justifyContent="start"
            p="20px"
            borderTop="1px solid"
            borderColor="brand.lightGray1"
            gap="20px"
          >
            <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
              Admin details
            </Text>

            <Grid
              templateColumns="1fr 1fr"
              rowGap="15px"
              columnGap="20px"
              wordBreak="break-word"
              w="full"
            >
              <GridItem>
                <Flex gap="5px" flexDir="column" w="full">
                  <Text color="brand.darkGray" textStyle="extraSmallRegular">
                    Role
                  </Text>
                  <Flex py="15px">
                    <Badge colorScheme="badge.invited" fontSize="12px">
                      Company Admin
                    </Badge>
                  </Flex>
                </Flex>
              </GridItem>
              <GridItem>
                <ProfileField
                  label="Can top up company points"
                  value={admin.canTopUpCompanyPoints ? 'Yes' : 'No'}
                />
              </GridItem>
            </Grid>
          </Flex>

          {/* Other details */}
          <Flex
            w="full"
            flexDir={{ base: 'column', md: 'row' }}
            justifyContent="start"
            p="20px"
            borderTop="1px solid"
            borderColor="brand.lightGray1"
            gap="20px"
          >
            <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
              Other details
            </Text>

            <Grid
              templateColumns="1fr 1fr"
              rowGap="15px"
              columnGap="20px"
              wordBreak="break-word"
              w="full"
            >
              <GridItem>
                <Flex gap="5px" flexDir="column" w="full">
                  <Text color="brand.darkGray" textStyle="extraSmallRegular">
                    Aslan Internal ID
                  </Text>
                  <Flex py="15px" align="center" gap="8px">
                    <Text textStyle="smallMedium">{admin.friendlyId || '-'}</Text>
                    <ActionIconButton
                      aria-label="Copy Aslan ID"
                      icon={
                        hasCopied ? <CheckOutline boxSize="16px" /> : <CopyOutline boxSize="16px" />
                      }
                      size="xs"
                      onClick={onCopy}
                      color={hasCopied ? 'green.500' : 'brand.darkGray'}
                    />
                  </Flex>
                </Flex>
              </GridItem>
              <GridItem>
                <ProfileField
                  label="Join date"
                  value={
                    admin.joinDate
                      ? format(parseISO(admin.joinDate), DEFAULT_DATE_FORMAT)
                      : undefined
                  }
                />
              </GridItem>
              <GridItem>
                <ProfileField
                  label="First invitation date"
                  value={
                    admin.invitedAt
                      ? format(parseISO(admin.invitedAt), DEFAULT_DATE_FORMAT)
                      : undefined
                  }
                />
              </GridItem>
            </Grid>
          </Flex>
        </Flex>
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default AdminProfile;
